import { useState, useEffect, useRef } from "react";
import { useNavigate } from "react-router-dom";
import { useDealer } from "src/hooks/useDealer";
import { PrimaryButton, GhostButton } from "src/components/Buttons";
import {
  Container,
  Body,
  Title,
  PorchPassLogo,
  LogoLink,
  Header,
  HeaderDescription,
  ButtonGroup,
  Section,
  SectionHeading,
  IconWrapper,
} from "./InvitePage.styles";
import { CloseIcon, Agreement03, Share04 } from "src/assets/icons";
import Input from "src/components/Input";
import { Logo } from "src/components/Logo";
import { useValidateDealerId } from "src/hooks/useValidateDealerId";
import { usePageView } from "src/hooks/usePageView";
import { useSafeApiCall, useApiClient } from "src/hooks/api";
import { API_PREFIX_PATH } from "src/constants";
import { Mail02, SmartPhoneIcon } from "src/assets/icons";
import LendersSection from "./LendersSection";
import { validateField, isFormValid, sendInviteRaw } from "./helpers";
import DuplicateBorrowerConfirmationDialog from "src/components/DuplicateBorrowerConfirmationDialog";
import { useToast } from "src/components/Toast";
import {
  isDuplicateCoborrowerConfirmableError,
  isDuplicatePrimaryBorrowerError,
} from "src/utils/duplicateBorrowerErrors";

export interface InviteLender {
  id: string;
  name: string;
  email: string;
  logo_url?: string;
  enabled: boolean;
  autoSubmit: boolean;
  isPrimary?: boolean;
}

export default () => {
  const [firstNameInput, setFirstNameInput] = useState("");
  const [lastNameInput, setLastNameInput] = useState("");
  const [emailOrPhoneInput, setEmailOrPhoneInput] = useState("");
  const [inputErrors, setInputErrors] = useState({
    firstName: "",
    lastName: "",
    email: "",
  });
  const [disabled, setDisabled] = useState(true);
  const [invalidLenderSelection, setInvalidLenderSelection] = useState(false);
  const [isDuplicateDialogOpen, setIsDuplicateDialogOpen] = useState(false);
  const [pendingInviteData, setPendingInviteData] = useState<{
    firstNameInput: string;
    lastNameInput: string;
    emailOrPhoneInput: string;
    lenders: InviteLender[];
  } | null>(null);
  const navigate = useNavigate();
  const { dealer_id } = useDealer();
  const safeApiCall = useSafeApiCall();
  const { fetchFromApi } = useApiClient();
  const { setPendingToast } = useToast();
  const [lenders, setLenders] = useState<InviteLender[]>([]);
  const [submitting, setSubmitting] = useState(false);
  const emailInputRef = useRef<HTMLDivElement>(null);
  const hasEnabledLenders = lenders.some((l) => l.enabled || l.isPrimary);
  const hasAvailableLenders = lenders.length > 0;

  usePageView("New Application Invite");
  useValidateDealerId();

  useEffect(() => {
    const path = `${API_PREFIX_PATH}/${dealer_id}/lenders`;
    void safeApiCall(() => fetchFromApi(path)).then((response) => {
      const data = response as {
        lenders: {
          id: string;
          name: string;
          email: string;
          logo_url?: string;
          enabled: boolean;
        }[];
        primary_lender: string;
      };
      const primaryId = data.primary_lender;
      const dealerEnabledLenders = data.lenders
        .filter((lender) => lender.enabled === true)
        .map((lender) => ({
          id: lender.id,
          name: lender.name,
          email: lender.email,
          logo_url: lender.logo_url,
          enabled: true,
          autoSubmit: false,
          isPrimary: lender.id === primaryId,
        }));

      setLenders(dealerEnabledLenders);
    });
  }, []);

  useEffect(() => {
    setInvalidLenderSelection(hasAvailableLenders && !hasEnabledLenders);

    setDisabled(
      !isFormValid({
        firstNameInput,
        lastNameInput,
        emailOrPhoneInput,
        lenders,
      }),
    );
  }, [
    firstNameInput,
    lastNameInput,
    emailOrPhoneInput,
    lenders,
    hasAvailableLenders,
    hasEnabledLenders,
  ]);

  const handleDialogConfirm = async () => {
    if (!pendingInviteData) return;

    try {
      setSubmitting(true);
      await sendInviteRaw({
        dealer_id: dealer_id ?? "",
        firstNameInput: pendingInviteData.firstNameInput,
        lastNameInput: pendingInviteData.lastNameInput,
        emailOrPhoneInput: pendingInviteData.emailOrPhoneInput,
        lenders: pendingInviteData.lenders,
        fetchFromApi,
        ignoreDuplicate: true,
      });
      setPendingToast("New application invite sent", "success");
      setIsDuplicateDialogOpen(false);
      setPendingInviteData(null);
      void navigate(-1);
    } catch (err) {
      const errorMsg =
        err instanceof Error ? err.message : "Invite failed to send";
      setPendingToast(errorMsg, "error");
      throw err;
    } finally {
      setSubmitting(false);
    }
  };

  const handleDialogCancel = () => {
    if (pendingInviteData) {
      const field = pendingInviteData.emailOrPhoneInput.includes("@")
        ? "email"
        : "email";
      setInputErrors((prev) => ({
        ...prev,
        [field]: "Already associated with another application as a co-borrower",
      }));
      // Scroll to email field to show error
      setTimeout(() => {
        emailInputRef.current?.scrollIntoView({
          behavior: "smooth",
          block: "center",
        });
      }, 0);
    }
    setIsDuplicateDialogOpen(false);
    setPendingInviteData(null);
  };

  const handleSubmit = async () => {
    if (!hasEnabledLenders) {
      setInvalidLenderSelection(true);
      return;
    }

    if (
      !isFormValid({
        firstNameInput,
        lastNameInput,
        emailOrPhoneInput,
        lenders,
      })
    )
      return;

    try {
      setSubmitting(true);
      await sendInviteRaw({
        dealer_id: dealer_id ?? "",
        firstNameInput,
        lastNameInput,
        emailOrPhoneInput,
        lenders,
        fetchFromApi,
      });
      setPendingToast("New application invite sent", "success");
      void navigate(-1);
    } catch (err) {
      const status =
        err && typeof err === "object" && "status" in err
          ? (err as { status: number }).status
          : undefined;
      const message = err instanceof Error ? err.message : "";

      // Check for duplicate primary borrower error - show field error directly
      if (status === 400 && isDuplicatePrimaryBorrowerError(message)) {
        const field = "email";
        setInputErrors((prev) => ({
          ...prev,
          [field]:
            "Already associated with another application's primary borrower",
        }));
        // Scroll to email field to show error
        setTimeout(() => {
          emailInputRef.current?.scrollIntoView({
            behavior: "smooth",
            block: "center",
          });
        }, 0);
      }
      // Check for duplicate co-borrower error - show dialog for confirmation
      else if (
        status === 400 &&
        isDuplicateCoborrowerConfirmableError(message)
      ) {
        setPendingInviteData({
          firstNameInput,
          lastNameInput,
          emailOrPhoneInput,
          lenders,
        });
        setIsDuplicateDialogOpen(true);
      } else {
        // Show generic error toast
        setPendingToast(message || "Invite failed to send", "error");
      }
    } finally {
      setSubmitting(false);
    }
  };

  const handleToggleEnabled = (lenderId: string, enabled: boolean) => {
    setLenders((prev) =>
      prev.map((l) => (l.id === lenderId ? { ...l, enabled } : l)),
    );
  };

  const handleToggleAutoSubmit = (lenderId: string, autoSubmit: boolean) => {
    setLenders((prev) =>
      prev.map((l) => (l.id === lenderId ? { ...l, autoSubmit } : l)),
    );
  };

  return (
    <Container>
      <Header>
        <HeaderDescription>
          <Agreement03 fill="#4075FF" /> New Application invite
        </HeaderDescription>
        <GhostButton
          initialBg={false}
          onClick={() => {
            void navigate(-1);
          }}
        >
          Close
          <CloseIcon />
        </GhostButton>
      </Header>
      <Body>
        <Title>
          Share a new application invite via email{" "}
          <IconWrapper>
            <Mail02 />
          </IconWrapper>{" "}
          or{" "}
          <IconWrapper>
            <SmartPhoneIcon />
          </IconWrapper>{" "}
          phone.
        </Title>
        <Section ref={emailInputRef}>
          <SectionHeading>Customer Details</SectionHeading>
          <Input
            className={inputErrors.firstName ? "wiggle-animation" : ""}
            showError={!!inputErrors.firstName}
            errorMessage={inputErrors.firstName}
            placeholder="First name"
            onChange={(e) => setFirstNameInput(e.target.value)}
            onFocus={() =>
              setInputErrors((prev) => ({ ...prev, firstName: "" }))
            }
            onBlur={() => {
              const error = validateField("firstName", firstNameInput);
              setInputErrors((prev) => ({ ...prev, firstName: error }));
            }}
          />

          <Input
            className={inputErrors.lastName ? "wiggle-animation" : ""}
            showError={!!inputErrors.lastName}
            errorMessage={inputErrors.lastName}
            placeholder="Last name"
            onChange={(e) => setLastNameInput(e.target.value)}
            onFocus={() => setInputErrors({ ...inputErrors, lastName: "" })}
            onBlur={() => {
              const error = validateField("lastName", lastNameInput);
              setInputErrors((prev) => ({ ...prev, lastName: error }));
            }}
          />
          <Input
            className={inputErrors.email ? "wiggle-animation" : ""}
            showError={!!inputErrors.email}
            errorMessage={inputErrors.email}
            placeholder="Email or phone"
            onChange={(e) => setEmailOrPhoneInput(e.target.value)}
            onFocus={() => setInputErrors({ ...inputErrors, email: "" })}
            onBlur={() => {
              const error = validateField("emailOrPhone", emailOrPhoneInput);
              setInputErrors((prev) => ({ ...prev, email: error }));
            }}
          />
        </Section>
        <Section>
          <LendersSection
            lenders={lenders}
            onToggleEnabled={handleToggleEnabled}
            onToggleAutoSubmit={handleToggleAutoSubmit}
            invalidSelection={invalidLenderSelection}
          />
        </Section>
        <ButtonGroup>
          <PrimaryButton
            size="large"
            onClick={() => void handleSubmit()}
            loading={submitting}
            disabled={disabled}
          >
            Send Invite
            <Share04 />
          </PrimaryButton>
          <GhostButton
            size="large"
            onClick={() => {
              void navigate(-1);
            }}
          >
            Cancel
          </GhostButton>
        </ButtonGroup>
      </Body>
      <PorchPassLogo>
        <LogoLink to="/">
          <Logo />
        </LogoLink>
      </PorchPassLogo>

      <DuplicateBorrowerConfirmationDialog
        open={isDuplicateDialogOpen}
        onOpenChange={(open) => {
          if (!open) {
            handleDialogCancel();
          }
        }}
        borrowerName={
          pendingInviteData
            ? `${pendingInviteData.firstNameInput} ${pendingInviteData.lastNameInput}`
            : ""
        }
        onConfirm={handleDialogConfirm}
      />
    </Container>
  );
};
